import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Lock, Loader2, ShieldCheck } from "lucide-react";

import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/shared/SEO";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { api, unwrap } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

export default function CheckoutPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    api.get(`/bookings/${id}/public`)
      .then((r) => setBooking(unwrap(r)))
      .catch(() => toast.error("Booking not found"))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (params.get("cancelled")) toast.message("Payment cancelled — you can retry below.");
  }, [params]);

  const handlePay = async () => {
    if (!agreed) {
      toast.error(t("checkout.agreement_required"));
      return;
    }
    setPaying(true);
    try {
      const r = await api.post(`/bookings/${id}/checkout`, {
        origin_url: window.location.origin,
      });
      const data = unwrap(r);
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Could not start checkout");
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#0EA5A4]" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <h1 className="font-display text-3xl font-bold">Booking not found</h1>
          <Button className="mt-4 rounded-xl bg-[#0EA5A4]" onClick={() => navigate("/book")}>Start over</Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="checkout-page">
      <SEO
        title="Review & pay — Ever Clean Home"
        description="Securely pay for your Ever Clean Home cleaning via Stripe. Payment info is encrypted and never touches our servers."
        noindex
      />
      <Header />
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-12">
        <button onClick={() => navigate("/book")} className="text-sm text-[#4B5563] hover:text-[#0EA5A4] flex items-center gap-1 mb-6" data-testid="checkout-back">
          <ArrowLeft className="w-4 h-4" /> {t("checkout.back")}
        </button>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter">{t("checkout.title")}</h1>
          <p className="mt-2 text-[#4B5563]">{t("checkout.subtitle")}</p>

          <Card className="mt-8 rounded-2xl p-6 border border-[#E5E7EB]">
            <div className="text-xs font-mono uppercase tracking-wider text-[#0EA5A4] font-bold">{t("checkout.summary_title")}</div>
            <div className="mt-2 font-display text-2xl font-bold" data-testid="checkout-booking-number">#{booking.booking_number}</div>
            <div className="mt-1 text-sm text-[#4B5563]">{booking.customer_name} · {booking.customer_email}</div>

            <div className="mt-5 grid grid-cols-2 gap-y-2 text-sm">
              <span className="text-[#4B5563]">Zone</span><span className="font-semibold text-right">{booking.zone_name}</span>
              <span className="text-[#4B5563]">Property</span><span className="font-semibold text-right capitalize">{booking.property_type}</span>
              <span className="text-[#4B5563]">Size</span><span className="font-semibold text-right">{booking.square_feet} sq ft</span>
              {booking.preferred_date && <><span className="text-[#4B5563]">Date</span><span className="font-semibold text-right">{booking.preferred_date}</span></>}
            </div>

            <div className="mt-6 pt-6 border-t border-[#E5E7EB] flex items-center justify-between">
              <span className="text-sm text-[#4B5563]">Total</span>
              <span className="font-mono text-4xl font-bold text-[#0EA5A4]" data-testid="checkout-total">
                ${booking.total.toFixed(2)} <span className="text-base text-[#4B5563]">{booking.currency}</span>
              </span>
            </div>
          </Card>

          <label className="mt-6 flex items-start gap-3 cursor-pointer" data-testid="checkout-agreement-label">
            <Checkbox
              checked={agreed}
              onCheckedChange={(v) => setAgreed(!!v)}
              className="mt-0.5"
              data-testid="checkout-agreement-checkbox"
            />
            <span className="text-sm text-[#4B5563]">
              {t("checkout.agreement_label")}{" "}
              <a
                href="/service-agreement"
                target="_blank"
                rel="noreferrer noopener"
                className="font-semibold text-[#0EA5A4] hover:underline"
                data-testid="checkout-agreement-link"
              >
                {t("checkout.agreement_link")}
              </a>
              .
            </span>
          </label>

          <Button onClick={handlePay} disabled={paying || !agreed}
            className="w-full mt-4 h-14 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white font-semibold text-base shadow-lg shadow-[#0EA5A4]/20 hover:scale-[1.01] transition-transform disabled:hover:scale-100"
            data-testid="pay-button">
            {paying ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> {t("checkout.creating")}</> : <><Lock className="w-4 h-4 mr-2" /> {t("checkout.pay_btn", { amount: `$${booking.total.toFixed(2)}` })}</>}
          </Button>

          <div className="mt-5 flex items-center justify-center gap-2 text-xs text-[#4B5563]">
            <ShieldCheck className="w-4 h-4 text-[#0EA5A4]" />
            Powered by <span className="font-semibold">Stripe</span> — your payment info is encrypted.
          </div>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
}
